import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes, css } from 'styled-components';

const Drawer = ({ anchor = 'right', open, onClose, children, width }) => {
  // Bloquear scroll cuando el drawer está abierto (mejorado para móviles)
  useEffect(() => {
    if (open) {
      // Guardar la posición actual del scroll
      const scrollY = window.scrollY;
      
      // Aplicar estilos para bloquear scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // Prevenir scroll en iOS Safari
      document.body.style.webkitOverflowScrolling = 'touch';
      
      return () => {
        // Restaurar estilos
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.style.webkitOverflowScrolling = '';
        
        // Restaurar posición del scroll
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  // Manejar tecla Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <DrawerOverlay onClick={onClose}>
      <DrawerContent
        $anchor={anchor}
        $width={width}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </DrawerContent>
    </DrawerOverlay>
  );
};

Drawer.propTypes = {
  anchor: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default Drawer;

// Animaciones
const slideInRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideInTop = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideInBottom = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Styled Components
const DrawerOverlay = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
  background-color: rgb(0 0 0 / 50%);
  inset: 0;
  position: fixed;
  z-index: 1000;
  touch-action: none; /* Prevenir gestos táctiles en móviles */
  -webkit-overflow-scrolling: touch;
`;

const DrawerContent = styled.div`
  position: fixed;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 10%), 0 10px 10px -5px rgb(0 0 0 / 4%);
  z-index: 1001;
  ${({ $anchor, $width }) => {
    const defaultWidth = '400px';
    const customWidth = $width ? (typeof $width === 'number' ? `${$width}px` : $width) : defaultWidth;
    switch ($anchor) {
      case 'right':
        return css`
          animation: ${slideInRight} 0.3s ease-out;
          height: 100vh;
          height: 100dvh;
          max-width: 90vw;
          right: 0;
          top: 0;
          width: ${customWidth};
        `;
      case 'left':
        return css`
          animation: ${slideInLeft} 0.3s ease-out;
          height: 100vh;
          height: 100dvh;
          left: 0;
          max-width: 90vw;
          top: 0;
          width: ${customWidth};
        `;
      case 'top':
        return css`
          animation: ${slideInTop} 0.3s ease-out;
          height: 400px;
          left: 0;
          max-height: 90vh;
          right: 0;
          top: 0;
        `;
      case 'bottom':
        return css`
          animation: ${slideInBottom} 0.3s ease-out;
          bottom: 0;
          height: 400px;
          left: 0;
          max-height: 90vh;
          right: 0;
        `;
      default:
        return css`
          animation: ${slideInRight} 0.3s ease-out;
          height: 100vh;
          max-width: 90vw;
          right: 0;
          top: 0;
          width: ${customWidth};
        `;
    }
  }}
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* Scroll suave en iOS */
  overscroll-behavior: contain; /* Prevenir scroll en el fondo */
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    ${({ $anchor, $width }) => {
    if ($anchor === 'left' || $anchor === 'right') {
      const mobileWidth = $width ? (typeof $width === 'number' ? `${Math.min($width, 320)}px` : $width) : '320px';
      return css`
          height: 100vh;
          height: 100dvh;
          max-width: 100vw;
          width: ${mobileWidth};
        `;
    }
    return '';
  }}
  }
`;